/**
 * @file     vowTermsAcceptance.ts
 * @purpose  A short-lived record that this browser already accepted the VOW
 *           terms before leaving to confirm an email, so resuming after the
 *           confirmation does not make the person scroll and accept again.
 *           Honored only for the same terms version and a few hours; cleared
 *           when registration completes. Browser storage only: the server
 *           still records acceptance when registration is sent.
 */
'use client';

import { VOW_TOU_VERSION } from '@/services/vow/vowTerms';

/** Sits beside VOW_PENDING_PHONE_KEY: both are parked on the check-email hand-off. */
export const VOW_TERMS_ACCEPTED_KEY = 'relta_vow_terms_accepted';

export const VOW_TERMS_ACCEPTANCE_TTL_MS = 6 * 60 * 60 * 1000;

export function writeVowTermsAcceptance(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(VOW_TERMS_ACCEPTED_KEY, JSON.stringify({ version: VOW_TOU_VERSION, at: now }));
  } catch {
    /* storage disabled: the terms are shown again */
  }
}

export function hasVowTermsAcceptance(now: number = Date.now()): boolean {
  try {
    const raw = window.localStorage.getItem(VOW_TERMS_ACCEPTED_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { version?: unknown; at?: unknown };
    return parsed.version === VOW_TOU_VERSION && typeof parsed.at === 'number' && now - parsed.at >= 0 && now - parsed.at < VOW_TERMS_ACCEPTANCE_TTL_MS;
  } catch {
    return false;
  }
}

export function clearVowTermsAcceptance(): void {
  try {
    window.localStorage.removeItem(VOW_TERMS_ACCEPTED_KEY);
  } catch {
    /* nothing to clear */
  }
}
